const PRODUCT_REVIEWS_API = 'https://api-cdn.yotpo.com';
const PRODUCT_REVIEWS_PER_PAGE = 4;
const PRODUCT_REVIEWS_CENTRE_FIT = '2';

class ProductReviews extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.list = this.querySelector('[data-reviews-list]');
    this.results = this.querySelector('.product-reviews_results');
    this.status = this.querySelector('[data-reviews-status]');
    this.empty = this.querySelector('[data-reviews-empty]');
    this.pagination = this.querySelector('[data-reviews-pagination]');
    this.pages = this.querySelector('[data-reviews-pages]');
    this.cardTemplate = this.querySelector('[data-review-template]');
    this.starIcons = this.querySelector('[data-star-icons]').content;
    this.fit = this.querySelector('.product-reviews_fit');
    this.fitSlider = this.querySelector('[data-fit-slider]');
    this.fitButtons = [...this.querySelectorAll('[data-fit]')];
    this.clearButtons = [...this.querySelectorAll('[data-reviews-clear]')];
    this.lightbox = this.querySelector('[data-review-lightbox]');
    this.cache = new Map();
    this.state = {
      page: 1,
      sort: this.querySelector('[data-dropdown="sort"] input:checked').value,
      score: '',
      fit: '',
    };

    this.querySelector('.product-reviews_write').addEventListener('click', (event) =>
      document.getElementById(this.dataset.reviewForm)?.open(event.currentTarget)
    );
    this.querySelectorAll('[data-dropdown]').forEach((dropdown) => this.bindDropdown(dropdown));
    this.bindFitSlider();
    this.bindLightbox();
    this.clearButtons.forEach((button) => button.addEventListener('click', () => this.clearFilters()));
    this.pagination.addEventListener('click', (event) => this.handlePageClick(event));
    document.addEventListener('click', (event) => this.scrollToReviews(event));
    document.addEventListener('click', (event) => {
      this.querySelectorAll('[data-dropdown][open]').forEach((dropdown) => {
        if (!dropdown.contains(event.target)) dropdown.open = false;
      });
    });

    this.init();
  }

  // The first, unfiltered page also carries the live rating and the Fit question id.
  async init() {
    await this.render();
    this.syncFit();
  }

  // The PDP stylesheet turns off smooth scrolling for the page, so links to the reviews scroll here.
  scrollToReviews(event) {
    const link = event.target.closest(`a[href="#${this.id}"]`);
    if (!link) return;
    event.preventDefault();
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
    this.querySelector('.product-reviews_heading').focus({ preventScroll: true });
  }

  /* ---- Live rating ---- */

  applyBottomline(bottomline) {
    const average = (bottomline.averageScore || 0).toFixed(1);
    const total = bottomline.totalReview || 0;
    document.querySelectorAll(`[data-reviews-rating][data-product-id="${this.dataset.productId}"]`).forEach((rating) => {
      rating.hidden = !total;
      rating.querySelector('[data-rating-value]').textContent = average;
      rating.querySelector('[data-rating-count]').textContent = total;
      const stars = rating.querySelector('[data-rating-stars]');
      stars.replaceChildren(...this.starSvgs(Number(average)));
      stars.setAttribute('aria-label', `${average} out of 5 stars, ${total} total reviews`);
    });

    const fields = bottomline.customFieldsBottomline || {};
    const key = Object.keys(fields).find((field) => fields[field].type === 'Size');
    this.fit.hidden = !key;
    if (key) this.fitQuestionId = Number(key.replace('--', ''));
  }

  // Same rounding as snippets/rating-stars.liquid: a half star for .3 to .7.
  starSvgs(rating) {
    const decimal = rating % 1;
    const full = Math.floor(rating) + (decimal > 0.7 ? 1 : 0);
    const half = decimal >= 0.3 && decimal <= 0.7;
    return Array.from({ length: 5 }, (_, index) => {
      const type = index < full ? 'full' : half && index === full ? 'half' : 'empty';
      return this.starIcons.querySelector(`[data-star="${type}"] svg`).cloneNode(true);
    });
  }

  /* ---- Fit slider: three stops, each one a filter ---- */

  bindFitSlider() {
    const stops = this.fitButtons.length;
    const stopAt = (event) => {
      const rect = this.fitSlider.getBoundingClientRect();
      const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
      return String(Math.round(ratio * (stops - 1)) + 1);
    };

    this.fitSlider.addEventListener('pointerdown', (event) => {
      if (!this.fitQuestionId) return;
      event.preventDefault();
      this.fitSlider.setPointerCapture(event.pointerId);
      this.fitSlider.classList.add('is-dragging');
      this.fitSlider.focus();
      this.previewFit(stopAt(event));
    });
    this.fitSlider.addEventListener('pointermove', (event) => {
      if (this.fitSlider.hasPointerCapture(event.pointerId)) this.previewFit(stopAt(event));
    });
    const release = (event) => {
      if (!this.fitSlider.hasPointerCapture(event.pointerId)) return;
      this.fitSlider.releasePointerCapture(event.pointerId);
      this.fitSlider.classList.remove('is-dragging');
      this.setFit(stopAt(event));
    };
    this.fitSlider.addEventListener('pointerup', release);
    this.fitSlider.addEventListener('pointercancel', () => {
      this.fitSlider.classList.remove('is-dragging');
      this.syncFit();
    });

    this.fitSlider.addEventListener('keydown', (event) => {
      const current = Number(this.state.fit || PRODUCT_REVIEWS_CENTRE_FIT);
      const next = {
        ArrowLeft: current - 1,
        ArrowDown: current - 1,
        ArrowRight: current + 1,
        ArrowUp: current + 1,
        PageDown: current - 1,
        PageUp: current + 1,
        Home: 1,
        End: stops,
      }[event.key];
      if (next === undefined) return;
      event.preventDefault();
      this.setFit(String(Math.min(Math.max(next, 1), stops)));
    });

    this.fitButtons.forEach((button) => button.addEventListener('click', () => this.setFit(button.dataset.fit)));
  }

  previewFit(fit) {
    this.fit.style.setProperty('--fit-position', (Number(fit) - 1) / (this.fitButtons.length - 1));
  }

  setFit(fit) {
    if (!this.fitQuestionId) return;
    const changed = fit !== this.state.fit;
    this.state.fit = fit;
    this.syncFit();
    if (changed) this.update({ page: 1 });
  }

  syncFit() {
    // With no Fit filter the dot rests in the centre.
    const fit = this.state.fit || PRODUCT_REVIEWS_CENTRE_FIT;
    const label = this.fitButtons.find((button) => button.dataset.fit === fit)?.textContent.trim();
    this.previewFit(fit);
    this.fit.removeAttribute('data-loading');
    this.fitSlider.setAttribute('aria-valuenow', fit);
    this.fitSlider.setAttribute('aria-valuetext', this.state.fit ? label : 'All fits');
    this.fitButtons.forEach((button) => {
      const pressed = button.dataset.fit === this.state.fit;
      button.setAttribute('aria-pressed', pressed);
      button.classList.toggle('link--underline', pressed);
    });
  }

  /* ---- Photo lightbox (native <dialog>: Escape and focus come with it) ---- */

  bindLightbox() {
    this.lightbox.querySelector('[data-lightbox-close]').addEventListener('click', () => this.lightbox.close());
    this.lightbox.querySelectorAll('[data-lightbox-step]').forEach((button) =>
      button.addEventListener('click', () => this.showPhoto(this.photoIndex + Number(button.dataset.lightboxStep)))
    );
    this.lightbox.addEventListener('click', (event) => event.target === this.lightbox && this.lightbox.close());
    this.lightbox.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.showPhoto(this.photoIndex - 1);
      if (event.key === 'ArrowRight') this.showPhoto(this.photoIndex + 1);
    });
    this.lightbox.addEventListener('close', () => {
      document.body.classList.remove('overflow-hidden');
      this.photoOpener?.focus();
    });
  }

  openPhotos(photos, index, author, opener) {
    this.photos = photos;
    this.photoAuthor = author;
    this.photoOpener = opener;
    this.lightbox.querySelectorAll('[data-lightbox-step]').forEach((button) => (button.hidden = photos.length < 2));
    this.showPhoto(index);
    document.body.classList.add('overflow-hidden');
    this.lightbox.showModal();
  }

  showPhoto(index) {
    if (!this.photos?.length) return;
    this.photoIndex = (index + this.photos.length) % this.photos.length;
    const photo = this.photos[this.photoIndex];
    const image = this.lightbox.querySelector('[data-lightbox-image]');
    // Clear first so the previous photo can't show while this one loads; the size keeps the frame steady.
    image.removeAttribute('src');
    image.width = photo.imageWidth || 1000;
    image.height = photo.imageHeight || 1000;
    image.src = photo.originalUrl;
    image.alt = `Photo ${this.photoIndex + 1} of ${this.photos.length} from ${this.photoAuthor}`;
  }

  /* ---- Rating / sort dropdowns ---- */

  // Arrow keys only move between options (native radios check as they go); a click, Enter or Space applies one.
  bindDropdown(dropdown) {
    let arrowed = false;
    this.markChecked(dropdown);
    dropdown.addEventListener('keydown', (event) => {
      if (event.key.startsWith('Arrow')) arrowed = true;
      if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('input')) {
        event.preventDefault();
        event.target.checked = true;
        this.commitDropdown(dropdown, event.target);
      }
    });
    dropdown.addEventListener('change', (event) => {
      this.markChecked(dropdown);
      if (arrowed) {
        arrowed = false;
        return;
      }
      this.commitDropdown(dropdown, event.target);
    });
    dropdown.addEventListener('keyup', (event) => {
      if (event.code !== 'Escape' || !dropdown.open) return;
      dropdown.open = false;
      dropdown.querySelector('summary').focus();
    });
  }

  commitDropdown(dropdown, input) {
    this.markChecked(dropdown);
    dropdown.querySelector('[data-dropdown-label]').textContent = input.dataset.label;
    dropdown.open = false;
    dropdown.querySelector('summary').focus();
    const key = dropdown.dataset.dropdown;
    if (this.state[key] !== input.value) this.update({ page: 1, [key]: input.value });
  }

  markChecked(dropdown) {
    dropdown.querySelectorAll('input').forEach((input) => input.nextElementSibling.classList.toggle('link--underline', input.checked));
  }

  clearFilters() {
    const scoreDropdown = this.querySelector('[data-dropdown="score"]');
    const all = scoreDropdown.querySelector('input[value=""]');
    all.checked = true;
    this.markChecked(scoreDropdown);
    scoreDropdown.querySelector('[data-dropdown-label]').textContent = all.dataset.label;
    this.state.fit = '';
    this.syncFit();
    this.update({ page: 1, score: '' });
  }

  /* ---- Pagination ---- */

  handlePageClick(event) {
    const button = event.target.closest('button');
    if (!button) return;
    const { page } = button.dataset;
    const target = page === 'prev' ? this.state.page - 1 : page === 'next' ? this.state.page + 1 : Number(page);
    const next = Math.min(Math.max(target, 1), this.pageCount || 1);
    if (next === this.state.page) return;
    // Page buttons are rebuilt on render, so focus moves to the new current page.
    this.focusCurrentPage = true;
    this.update({ page: next });
  }

  // 1 2 3 ... 12, with the current page and its neighbours always shown.
  pageNumbers(current, total) {
    if (total <= 5) return Array.from({ length: total }, (_, index) => index + 1);
    const start = Math.max(2, Math.min(current - 1, total - 3));
    const end = Math.min(total - 1, Math.max(current + 1, 3));
    const numbers = [1];
    if (start > 2) numbers.push('...');
    for (let page = start; page <= end; page++) numbers.push(page);
    if (end < total - 1) numbers.push('...');
    numbers.push(total);
    return numbers;
  }

  renderPagination(total) {
    const pageCount = Math.ceil(total / PRODUCT_REVIEWS_PER_PAGE);
    this.pageCount = pageCount;
    this.pagination.hidden = pageCount < 2;
    if (pageCount < 2) return;

    this.pagination.querySelector('[data-page="prev"]').disabled = this.state.page === 1;
    this.pagination.querySelector('[data-page="next"]').disabled = this.state.page === pageCount;
    this.pages.replaceChildren(
      ...this.pageNumbers(this.state.page, pageCount).map((page) => {
        const item = document.createElement('li');
        if (page === '...') {
          item.textContent = '...';
          item.setAttribute('aria-hidden', 'true');
          return item;
        }
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-reviews_page unstyled-button link';
        button.dataset.page = page;
        button.textContent = page;
        button.setAttribute('aria-label', `Page ${page}`);
        if (page === this.state.page) {
          button.setAttribute('aria-current', 'page');
          button.classList.add('link--underline');
        }
        item.appendChild(button);
        return item;
      })
    );
  }

  /* ---- Reviews ---- */

  update(changes) {
    Object.assign(this.state, changes);
    this.render();
  }

  fetchReviews({ page, sort, score, fit }) {
    const { appKey, productId } = this.dataset;
    const url = `${PRODUCT_REVIEWS_API}/v3/storefront/store/${appKey}/product/${productId}/reviews?page=${page}&perPage=${PRODUCT_REVIEWS_PER_PAGE}&sort=${sort}`;
    const filters = {};
    if (score) filters.scores = [Number(score)];
    if (fit) filters.crfs = [{ questionId: this.fitQuestionId, answers: [fit] }];

    const key = `${url}|${JSON.stringify(filters)}`;
    if (!this.cache.has(key)) {
      const request = Object.keys(filters).length
        ? fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(filters) })
        : fetch(url);
      this.cache.set(
        key,
        request
          .then((response) => {
            if (!response.ok) throw new Error(`Yotpo responded ${response.status}`);
            return response.json();
          })
          .catch((error) => {
            this.cache.delete(key);
            throw error;
          })
      );
    }
    return this.cache.get(key);
  }

  async render() {
    const request = (this.request = this.fetchReviews(this.state));
    const filtered = Boolean(this.state.score || this.state.fit);
    this.clearButtons.forEach((button) => (button.hidden = !filtered));
    this.results.classList.add('is-loading');
    this.results.setAttribute('aria-busy', 'true');

    try {
      const data = await request;
      if (request !== this.request) return;

      const total = data.pagination?.total || 0;
      if (!this.bottomlineApplied && !this.state.score && !this.state.fit) {
        this.bottomlineApplied = true;
        this.applyBottomline(data.bottomline || {});
      }
      this.list.replaceChildren(...(data.reviews || []).map((review) => this.renderCard(review)));
      this.renderEmpty(total);
      this.renderPagination(total);
      if (this.focusCurrentPage) this.pages.querySelector('[aria-current="page"]')?.focus();
      this.focusCurrentPage = false;

      const first = (this.state.page - 1) * PRODUCT_REVIEWS_PER_PAGE + 1;
      this.status.textContent = total
        ? `Showing ${first} to ${first + data.reviews.length - 1} of ${total} reviews`
        : this.empty.querySelector('[data-reviews-empty-text]').textContent;
    } catch (error) {
      if (request !== this.request) return;
      console.error(error);
      if (!this.bottomlineApplied) this.fit.hidden = true;
      this.list.replaceChildren();
      this.renderEmpty(0, 'Reviews could not be loaded. Please try again later.');
      this.pagination.hidden = true;
    } finally {
      if (request === this.request) {
        this.results.classList.remove('is-loading');
        this.results.removeAttribute('aria-busy');
        this.results.classList.add('is-ready');
      }
    }
  }

  renderEmpty(total, message) {
    const filtered = Boolean(this.state.score || this.state.fit);
    this.empty.hidden = total > 0;
    this.empty.querySelector('[data-reviews-empty-text]').textContent =
      message || (filtered ? 'No reviews match these filters.' : 'No reviews yet. Be the first to write one.');
  }

  renderCard(review) {
    const card = this.cardTemplate.content.firstElementChild.cloneNode(true);
    card.querySelector('[data-review-name]').textContent = review.user?.displayName || 'Anonymous';
    card.querySelector('[data-review-verified]').hidden = !review.verifiedBuyer;
    card.querySelector('[data-review-title]').textContent = review.title || '';
    card.querySelector('[data-review-body]').textContent = review.content || '';

    const author = review.user?.displayName || 'Anonymous';
    const photos = (review.imagesData || []).filter((photo) => photo.thumbUrl && photo.originalUrl);
    const photoList = card.querySelector('[data-review-photos]');
    photoList.hidden = !photos.length;
    photos.forEach((photo, index) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      const image = document.createElement('img');
      button.type = 'button';
      button.className = 'product-reviews_photo unstyled-button';
      button.setAttribute('aria-label', `View photo ${index + 1} of ${photos.length} from ${author}`);
      button.addEventListener('click', () => this.openPhotos(photos, index, author, button));
      image.src = photo.thumbUrl;
      image.alt = '';
      image.width = 112;
      image.height = 112;
      image.loading = 'lazy';
      button.appendChild(image);
      item.appendChild(button);
      photoList.appendChild(item);
    });

    const stars = card.querySelector('[data-review-stars]');
    stars.setAttribute('aria-label', `${review.score} out of 5 stars`);
    stars.append(...this.starSvgs(review.score));

    // Height, Body type and other choice questions; Fit is the slider's filter instead.
    const answers = card.querySelector('[data-review-answers]');
    Object.values(review.customFields || {})
      .filter((field) => ['SingleChoice', 'MultipleChoice'].includes(field.fieldType) && field.value)
      .forEach((field) => {
        const row = document.createElement('div');
        const title = document.createElement('dt');
        const value = document.createElement('dd');
        title.textContent = field.title;
        value.textContent = String(field.value).trim();
        row.append(title, value);
        answers.appendChild(row);
      });
    answers.hidden = !answers.children.length;
    return card;
  }
}

customElements.define('product-reviews', ProductReviews);
